import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { API, useAuth, apiPortraitUrl } from "@/auth";
import BattleStage from "@/components/BattleStage";
import ShareBattleButton from "@/components/ShareBattleButton";

/* ============================================================
 /pvp/:id — Challenge detail + response page

 Three modes:
 • You're the challenger, waiting for a response → view your locked verse + status
 • You're the challenged, need to respond → show challenger's verse + audio + response form
 • Everyone else (or completed) → full read-only battle recap
 ============================================================ */

export default function PvpDetail() {
 const { id } = useParams();
 const { user } = useAuth();
 const [c, setC] = useState(null);
 const [notFound, setNotFound] = useState(false);
 const [tick, setTick] = useState(0);

 // Poll while in-progress
 useEffect(() => {
 let cancelled = false;
 let iv;
 const load = () =>
 axios.get(`${API}/challenges/${id}`)
 .then(r => { if (!cancelled) setC(r.data); })
 .catch(() => { if (!cancelled) setNotFound(true); });
 load();
 iv = setInterval(load, 6000);
 return () => { cancelled = true; clearInterval(iv); };
 }, [id, tick]);

 if (notFound) {
 return (
 <div className="max-w-3xl mx-auto px-6 py-24 text-center">
 <h1 className="font-display text-4xl mb-4">CHALLENGE NOT FOUND</h1>
 <Link to="/rappers" className="text-yt-yellow underline font-mono">← BACK</Link>
 </div>
 );
 }
 if (!c) {
 return <div className="max-w-4xl mx-auto px-6 py-16 font-mono text-yt-muted animate-pulse-red">LOADING...</div>;
 }

 const uid = user ? user.rapper_name : null;
 const isChallenger = uid && uid === c.challenger_name;
 const isChallenged = uid && uid === c.challenged_name;
 const canRespond = isChallenged && (c.status === "pending" || c.status === "scheduled");
 const canSchedule = isChallenged && c.status === "pending";

 return (
 <div className="max-w-6xl mx-auto px-6 md:px-10 py-8">
 <ArenaHeader c={c} />

 {/* Video-game face-off + lipsync reel (only when completed) */}
 {c.status === "completed" && (c.challenger_lipsync || c.challenged_lipsync) ? (
 <BattleReel c={c} />
 ) : (
 <div className="grid grid-cols-2 gap-4 mb-8">
 <Fighter side="challenger" c={c} />
 <Fighter side="challenged" c={c} />
 </div>
 )}

 {/* Status ribbon */}
 <StatusRibbon c={c} />

 {/* Verses */}
 <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
 <VerseCard title={`${c.challenger_name}'S VERSE`} verse={c.challenger_verse}
 audio={c.challenger_audio} lipsync={c.challenger_lipsync} testid="verse-a" color="text-yt-yellow" />
 <VerseCard
 title={`${c.challenged_name}'S VERSE`}
 verse={c.status === "completed" || isChallenged ? c.challenged_verse : null}
 audio={c.challenged_audio}
 lipsync={c.challenged_lipsync}
 testid="verse-b" color="text-yt-red"
 placeholder={
 canRespond ? "Your turn to spit. Scroll down." :
 c.status === "pending" ? `${c.challenged_name} hasn't answered yet.` :
 c.status === "expired" ? "Time expired — no response." :
 c.status === "declined" ? `${c.challenged_name} declined the smoke.` : null
 }
 />
 </div>

 {/* Response form (challenged rapper only, still pending or scheduled) */}
 {canRespond && (
 <ResponseForm challengeId={c.id} status={c.status} scheduledAt={c.scheduled_at} canSchedule={canSchedule} onDone={() => setTick(t => t + 1)} />
 )}

 {/* Scheduled countdown card (visible to both parties, non-completed) */}
 {c.status === "scheduled" && c.scheduled_at && (
 <ScheduledCountdown scheduledAt={c.scheduled_at} isChallenged={isChallenged} />
 )}

 {/* Verdict */}
 {c.status === "completed" && (
 <Verdict c={c} />
 )}
 </div>
 );
}

function ArenaHeader({ c }) {
 return (
 <div className="mb-6">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-1"> PVP CHALLENGE</div>
 <h1 className="font-display text-4xl md:text-6xl tracking-tight text-white">
 {c.challenger_name} <span className="text-yt-red mx-2">VS</span> {c.challenged_name}
 </h1>
 <div className="font-mono text-xs text-yt-yellow tracking-widest mt-2">
 {c.arena_name?.toUpperCase()}
 </div>
 {c.trash_talk && (
 <div className="mt-3 border-l-4 border-yt-yellow bg-black/50 pl-3 py-2 max-w-2xl">
 <div className="font-mono text-[10px] text-yt-muted tracking-widest">OPENING JAB</div>
 <div className="font-mono italic text-white">&ldquo;{c.trash_talk}&rdquo;</div>
 </div>
 )}
 </div>
 );
}

function Fighter({ side, c }) {
 const key = side; // "challenger" | "challenged"
 const name = c[`${key}_name`];
 const portrait = c[`${key}_portrait`];
 const avatar = c[`${key}_avatar`];
 const style = c[`${key}_style`];
 const isWinner = c.winner_key === side;
 return (
 <div className={`bg-yt-surface border-2 ${isWinner ? "border-yt-yellow shadow-brutal-yellow" : "border-yt-border"} p-3`}>
 <div className="aspect-[3/4] bg-black border border-yt-border overflow-hidden relative mb-2">
 {portrait ? (
 <img src={apiPortraitUrl(portrait)} alt={name} className="w-full h-full object-cover" />
 ) : (
 <div className="w-full h-full flex items-center justify-center bg-yt-surface font-display text-2xl text-yt-yellow">{(name || "??").slice(0, 2).toUpperCase()}</div>
 )}
 {isWinner && (
 <div className="absolute top-2 right-2 bg-yt-yellow text-black text-xs px-2 py-1 font-mono tracking-widest">
 ★ WINNER
 </div>
 )}
 </div>
 <Link to={`/rapper/${encodeURIComponent(name)}`}
 className="block font-display text-2xl md:text-3xl text-white hover:text-yt-yellow truncate">
 {name}
 </Link>
 <div className="font-mono text-[10px] text-yt-yellow tracking-widest truncate">{style?.toUpperCase()}</div>
 </div>
 );
}

function StatusRibbon({ c }) {
 const state = c.status;
 const schedTime = c.scheduled_at ? new Date(c.scheduled_at).toLocaleString(undefined, {
   weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
 }) : null;
 const map = {
 pending: { text: `Waiting for ${c.challenged_name} to respond (48h)`, color: "border-yt-yellow text-yt-yellow" },
 scheduled: { text: `Battle scheduled for ${schedTime || "later"}`, color: "border-yt-yellow text-yt-yellow" },
 responded: { text: "Response received. Stitching audio...", color: "border-yt-yellow text-yt-yellow animate-pulse-red" },
 judging: { text: "Judge DeGagne is scoring both verses...", color: "border-yt-yellow text-yt-yellow animate-pulse-red" },
 completed: { text: `Verdict is in. Winner: ${c.winner_name}`, color: "border-yt-judge text-yt-judge" },
 declined: { text: `${c.challenged_name} declined this challenge.`, color: "border-yt-red text-yt-red" },
 expired: { text: "Challenge expired — no response within 48h.", color: "border-yt-muted text-yt-muted" },
 };
 const s = map[state] || { text: state.toUpperCase(), color: "border-white text-white" };
 return (
 <div data-testid="pvp-status-ribbon" className={`bg-black/50 border-2 px-4 py-3 font-mono text-sm tracking-widest ${s.color}`}>
 ● {s.text.toUpperCase()}
 </div>
 );
}

function ScheduledCountdown({ scheduledAt, isChallenged }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  const target = new Date(scheduledAt).getTime();
  const diff = target - now;
  const past = diff <= 0;
  const abs = Math.abs(diff);
  const d = Math.floor(abs / 86400000);
  const h = Math.floor((abs % 86400000) / 3600000);
  const m = Math.floor((abs % 3600000) / 60000);
  const s = Math.floor((abs % 60000) / 1000);
  const label = past ? "BATTLE WINDOW OPEN — DROP YOUR VERSE" : "BATTLE STARTS IN";
  const timeStr = past
    ? `${d}d ${h}h ${m}m past · ${isChallenged ? "24h grace to drop bars" : "waiting on challenger"}`
    : (d > 0 ? `${d}d ${h}h ${m}m ${s}s` : `${h}h ${m}m ${s}s`);
  return (
    <div data-testid="pvp-scheduled-countdown" className={`mt-6 bg-black border-4 ${past ? "border-yt-red animate-pulse-red" : "border-yt-yellow"} shadow-brutal p-6 text-center`}>
      <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2">● SCHEDULED BATTLE</div>
      <div className="font-display text-2xl md:text-3xl text-white mb-2">{label}</div>
      <div className={`font-display text-4xl md:text-6xl tracking-tight ${past ? "text-yt-red" : "text-yt-yellow"}`} data-testid="pvp-countdown-timer">{timeStr}</div>
      <div className="mt-3 font-mono text-xs text-yt-muted">
        {new Date(scheduledAt).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
      </div>
    </div>
  );
}

function VerseCard({ title, verse, audio, lipsync, testid, color, placeholder }) {
 const audioRef = useRef(null);
 // Prefer inline lipsync video if available (has audio baked in)
 return (
 <div data-testid={testid} className="bg-yt-surface border border-yt-border p-4">
 <div className={`font-mono text-xs tracking-widest ${color} mb-3`}>{title}</div>
 {verse ? (
 <>
 {lipsync ? (
 <video src={lipsync} controls playsInline preload="metadata" className="w-full mb-3 bg-black" />
 ) : audio ? (
 <audio ref={audioRef} src={audio} controls className="w-full mb-3" />
 ) : null}
 <pre className="font-mono text-sm text-white whitespace-pre-wrap leading-tight">{verse}</pre>
 </>
 ) : (
 <div className="font-mono text-yt-muted italic">{placeholder || "..."}</div>
 )}
 </div>
 );
}

/* ============================================================
 BattleReel — auto-sequential lipsync face-off using BattleStage.
 Plays challenger lipsync → challenged lipsync → verdict.
 ============================================================ */
function BattleReel({ c }) {
 const [phase, setPhase] = useState("intro"); // intro | play_a | play_b | verdict | done
 const [muted, setMuted] = useState(true);
 const judgeRef = useRef(null);

 // Kick reel: intro (2s) → play_a → play_b → verdict → done
 useEffect(() => {
 const t = setTimeout(() => setPhase("play_a"), 2000);
 return () => clearTimeout(t);
 }, []);

 // Safety timeouts so playback advances even if <video> onEnded misfires
 useEffect(() => {
 if (phase === "play_a") {
 const t = setTimeout(() => setPhase(p => p === "play_a" ? "play_b" : p), 45000);
 return () => clearTimeout(t);
 }
 if (phase === "play_b") {
 const t = setTimeout(() => setPhase(p => p === "play_b" ? "verdict" : p), 45000);
 return () => clearTimeout(t);
 }
 if (phase === "verdict") {
 if (judgeRef.current && c.judge_audio) {
 judgeRef.current.muted = muted;
 judgeRef.current.play().catch(() => {});
 }
 const t = setTimeout(() => setPhase("done"), c.judge_audio ? 12000 : 4000);
 return () => clearTimeout(t);
 }
 }, [phase, muted, c.judge_audio]);

 const unmute = () => {
 setMuted(false);
 // Judge audio should only be audible during the verdict phase — flip
 // muted regardless so it's ready, but only KICK play() if it's actively
 // that phase.
 if (judgeRef.current) {
 judgeRef.current.muted = false;
 if (phase === "verdict") judgeRef.current.play().catch(() => {});
 }
 // BUG FIX: Previously this loop called .play() on every <video> on the
 // page, which meant BOTH fighter lipsync MP4s played at the same time
 // (both rappers audible simultaneously). Now we only unmute the ACTIVE
 // side's lipsync — the passive fighter's video stays muted so we get
 // clean turn-based playback.
 document.querySelectorAll('video').forEach(v => {
 const isActive = v.dataset?.stageRole === "lipsync-active";
 if (isActive) v.muted = false;
 v.play().catch(() => {});
 });
 };

 const centerCue = (
 phase === "intro" ? "● ROLLING TAPE..." :
 phase === "play_a" ? `● ${c.challenger_name?.toUpperCase()} · LIVE` :
 phase === "play_b" ? `● ${c.challenged_name?.toUpperCase()} · LIVE` :
 phase === "verdict" ? "JUDGE DEGAGNE" :
 phase === "done" ? `★ ${c.winner_name?.toUpperCase()}` :
 null
 );

 return (
 <div data-testid="pvp-battle-reel" className="mb-6">
 <BattleStage
 fullscreen={false}
 arenaImage={c.arena_image}
 arenaName={c.arena_name}
 round={1}
 centerCue={centerCue}
 timerLeft="LIVE"
 timerRight="LIVE"
 fighterA={{
 name: c.challenger_name,
 portrait: c.challenger_portrait,
 lipsync: c.challenger_lipsync,
 rapping: phase === "play_a",
 onEnded: () => setPhase("play_b"),
 isWinner: c.winner_key === "challenger",
 }}
 fighterB={{
 name: c.challenged_name,
 portrait: c.challenged_portrait,
 lipsync: c.challenged_lipsync,
 rapping: phase === "play_b",
 onEnded: () => setPhase("verdict"),
 isWinner: c.winner_key === "challenged",
 }}
 verseLine={null}
 judgeComment={null}
 purseLeft={c.status === "completed" ? "PURSE PAID" : null}
 purseRight={null}
 muted={muted}
 onUnmute={muted ? unmute : null}
 extraTopRight={phase === "done" ? (
 <div className="flex flex-col gap-2 items-end">
 <ShareBattleButton compact />
 <button data-testid="pvp-reel-replay" onClick={() => setPhase("intro")}
 className="border-2 border-white text-white font-mono text-xs tracking-widest px-3 py-2 hover:bg-white hover:text-black bg-black/80">
 ↻ REPLAY REEL
 </button>
 <Link
 to="/battles/new"
 data-testid="pvp-battle-again-btn"
 className="border-2 border-yt-red text-yt-red bg-black/80 font-mono text-xs tracking-widest px-3 py-2 hover:bg-yt-red hover:text-white text-center">
 ▶ BATTLE AGAIN
 </Link>
 <Link
 to="/"
 data-testid="pvp-back-home-btn"
 className="border-2 border-yt-yellow text-yt-yellow bg-black/80 font-mono text-xs tracking-widest px-3 py-2 hover:bg-yt-yellow hover:text-black text-center">
 ◀ BACK TO HOME
 </Link>
 </div>
 ) : null}
 />
 {/* Hidden verdict audio */}
 {c.judge_audio && (
 <audio ref={judgeRef} src={c.judge_audio}
 onEnded={() => setPhase("done")}
 onError={() => setPhase("done")} className="hidden" />
 )}
 </div>
 );
}

function ResponseForm({ challengeId, status, scheduledAt, canSchedule, onDone }) {
 const [mode, setMode] = useState("respond"); // "respond" | "schedule"
 const [verse, setVerse] = useState("");
 const [busy, setBusy] = useState(false);
 const [err, setErr] = useState("");

 // Schedule state — default to tomorrow @ 8pm local
 const defaultSched = () => {
   const d = new Date();
   d.setDate(d.getDate() + 1);
   d.setHours(20, 0, 0, 0);
   const off = d.getTimezoneOffset();
   const local = new Date(d.getTime() - off * 60000);
   return local.toISOString().slice(0, 16);
 };
 const [schedInput, setSchedInput] = useState(defaultSched);

 const submit = async () => {
 setErr("");
 if (verse.trim().length < 20) {
 setErr("Give me at least 4 real bars.");
 return;
 }
 setBusy(true);
 try {
 await axios.post(`${API}/challenges/${challengeId}/respond`, { verse: verse.trim() });
 onDone();
 } catch (e) {
 setErr(e.response?.data?.detail || "Response failed.");
 } finally {
 setBusy(false);
 }
 };

 const submitSchedule = async () => {
   setErr("");
   if (!schedInput) { setErr("Pick a date and time."); return; }
   // Convert local datetime-local input into UTC ISO string
   const localDate = new Date(schedInput);
   if (isNaN(localDate.getTime())) { setErr("Invalid date."); return; }
   const iso = localDate.toISOString();
   setBusy(true);
   try {
     await axios.post(`${API}/challenges/${challengeId}/schedule`, { scheduled_at: iso });
     onDone();
   } catch (e) {
     setErr(e.response?.data?.detail || "Schedule failed.");
   } finally {
     setBusy(false);
   }
 };

 const decline = async () => {
 if (!confirm("Decline this challenge? This can't be undone.")) return;
 setBusy(true);
 try {
 await axios.post(`${API}/challenges/${challengeId}/decline`);
 onDone();
 } catch (e) {
 setErr(e.response?.data?.detail || "Decline failed.");
 } finally {
 setBusy(false);
 }
 };

 // Compute min/max for the datetime-local input (30min from now → 7 days from now)
 const minSched = (() => {
   const d = new Date(Date.now() + 30 * 60 * 1000);
   const off = d.getTimezoneOffset();
   return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
 })();
 const maxSched = (() => {
   const d = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
   const off = d.getTimezoneOffset();
   return new Date(d.getTime() - off * 60000).toISOString().slice(0, 16);
 })();

 return (
 <div className="mt-8 bg-yt-surface border-4 border-yt-red shadow-brutal p-6">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> YOUR RESPONSE</div>
 <h3 className="font-display text-3xl mb-4">
   {status === "scheduled" ? "DROP YOUR BARS" : "STEP IN OR SCHEDULE"}
 </h3>

 {/* Mode tabs — only show if user can still schedule (pending state) */}
 {canSchedule && (
   <div className="flex gap-2 mb-5">
     <button data-testid="pvp-mode-respond" onClick={() => { setMode("respond"); setErr(""); }}
       className={`px-4 py-2 font-mono text-xs tracking-widest border-2 transition-colors ${
         mode === "respond" ? "border-yt-yellow bg-yt-yellow text-black" : "border-yt-border text-white hover:border-yt-yellow"
       }`}>
       ▶ DROP BARS NOW
     </button>
     <button data-testid="pvp-mode-schedule" onClick={() => { setMode("schedule"); setErr(""); }}
       className={`px-4 py-2 font-mono text-xs tracking-widest border-2 transition-colors ${
         mode === "schedule" ? "border-yt-yellow bg-yt-yellow text-black" : "border-yt-border text-white hover:border-yt-yellow"
       }`}>
       ▶ SCHEDULE BATTLE
     </button>
   </div>
 )}

 {mode === "respond" || !canSchedule ? (
   <>
     {status === "scheduled" && scheduledAt && (
       <div className="mb-4 border-l-4 border-yt-yellow bg-black/50 pl-3 py-2">
         <div className="font-mono text-[10px] text-yt-muted tracking-widest">BATTLE SCHEDULED FOR</div>
         <div className="font-mono text-sm text-yt-yellow">
           {new Date(scheduledAt).toLocaleString(undefined, { weekday: "long", month: "long", day: "numeric", hour: "numeric", minute: "2-digit" })}
         </div>
         <div className="font-mono text-[10px] text-white mt-1">You can drop your verse any time on or before this window closes.</div>
       </div>
     )}
     <textarea data-testid="pvp-response-verse"
     value={verse} rows={10} maxLength={2500}
     onChange={e => setVerse(e.target.value)}
     placeholder="Answer back. Once you send, this verse is locked in and Judge DeGagne scores both."
     className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow text-white font-mono p-3 resize-none mb-3" />
     <div className="font-mono text-[10px] text-yt-muted mb-3">{verse.length}/2500</div>
     {err && <div data-testid="pvp-response-err" className="font-mono text-xs text-yt-red mb-3">{err}</div>}
     <div className="flex flex-wrap gap-3">
     <button onClick={submit} disabled={busy} data-testid="pvp-response-submit"
     className="bg-yt-yellow text-black font-display text-xl tracking-wider px-6 py-3 shadow-brutal disabled:opacity-50">
     {busy ? "SENDING..." : "SEND RESPONSE"}
     </button>
     <button onClick={decline} disabled={busy} data-testid="pvp-response-decline"
     className="border-2 border-yt-red text-yt-red font-mono text-xs tracking-widest px-4 py-3 hover:bg-yt-red hover:text-white transition-colors">
     DECLINE
     </button>
     </div>
   </>
 ) : (
   <>
     <div className="mb-3 font-mono text-sm text-white">
       Pick a date &amp; time (30 min to 7 days out). Once locked, the challenger sees a live countdown and can't back out.
     </div>
     <label className="block font-mono text-xs tracking-widest text-yt-yellow mb-2">BATTLE DATE &amp; TIME</label>
     <input data-testid="pvp-schedule-input" type="datetime-local"
       value={schedInput}
       min={minSched}
       max={maxSched}
       onChange={e => setSchedInput(e.target.value)}
       className="w-full bg-black border-2 border-yt-border focus:border-yt-yellow text-white font-mono p-3 mb-3" />
     {err && <div data-testid="pvp-schedule-err" className="font-mono text-xs text-yt-red mb-3">{err}</div>}
     <div className="flex flex-wrap gap-3">
       <button onClick={submitSchedule} disabled={busy} data-testid="pvp-schedule-submit"
         className="bg-yt-yellow text-black font-display text-xl tracking-wider px-6 py-3 shadow-brutal disabled:opacity-50">
         {busy ? "SCHEDULING..." : "LOCK IN BATTLE TIME"}
       </button>
       <button onClick={decline} disabled={busy} data-testid="pvp-schedule-decline"
         className="border-2 border-yt-red text-yt-red font-mono text-xs tracking-widest px-4 py-3 hover:bg-yt-red hover:text-white transition-colors">
         DECLINE
       </button>
     </div>
   </>
 )}
 </div>
 );
}

function Verdict({ c }) {
 const [playing, setPlaying] = useState(false);
 const aRef = useRef(null);
 useEffect(() => {
 if (playing && aRef.current) aRef.current.play().catch(() => {});
 }, [playing]);
 return (
 <div className="mt-8 bg-black border-4 border-yt-judge shadow-brutal p-6">
 <div className="font-mono text-xs tracking-[0.4em] text-yt-red mb-2"> VERDICT</div>
 <h3 className="font-display text-3xl md:text-4xl text-yt-judge mb-4">
 JUDGE DEGAGNE
 </h3>

 {c.judge_audio && (
 <div className="mb-4">
 <audio ref={aRef} src={c.judge_audio} controls className="w-full" onPlay={() => setPlaying(true)} />
 </div>
 )}

 <div className="font-mono text-white text-sm mb-6">{c.judge_verdict}</div>

 <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
 <ScoreCard name={c.challenger_name} judge={c.judge_a} winner={c.winner_key === "challenger"} />
 <ScoreCard name={c.challenged_name} judge={c.judge_b} winner={c.winner_key === "challenged"} />
 </div>
 </div>
 );
}

function ScoreCard({ name, judge, winner }) {
 return (
 <div className={`bg-yt-surface border-2 ${winner ? "border-yt-yellow" : "border-yt-border"} p-4`}>
 <div className="font-display text-2xl text-white mb-2">
 {name} {winner && <span className="text-yt-yellow">★</span>}
 </div>
 <div className="grid grid-cols-5 gap-1 mb-2">
 {[["FLO", judge?.flow], ["RHY", judge?.rhyme], ["TPC", judge?.topic],
 ["PCH", judge?.punchlines], ["CRD", judge?.crowd]].map(([k, v]) => (
 <div key={k} className="border border-yt-border bg-black py-1 text-center">
 <div className="font-display text-lg text-yt-yellow leading-none">{v ?? 0}</div>
 <div className="font-mono text-[8px] text-yt-muted tracking-widest">{k}</div>
 </div>
 ))}
 </div>
 <div className="font-mono text-sm text-yt-judge">{judge?.total ?? 0}/100</div>
 </div>
 );
}
